/**
 * RuleResultCard — displays a single rule evaluation result.
 *
 * Cards start collapsed; click to expand reasoning and evidence.
 */

import React, { useCallback, useState } from 'react';
import { Badge } from '@e14c/feathers';
import {
  CheckmarkFilled,
  CloseFilled,
  SubtractAlt,
  ChevronDown,
  ChevronUp,
} from '@e14c/feathers/icons';
import type { ReviewResultOut, ReviewVerdict } from '../types';
import { highlightEvidenceQuote } from '../utils/wordHighlight';

interface RuleResultCardProps {
  ruleTitle: string;
  result: ReviewResultOut | null;
  pending?: boolean;
}

type VerdictMeta = {
  badgeVariant: 'teal' | 'rose' | 'secondary';
  label: string;
};

const VERDICT_META: Record<ReviewVerdict, VerdictMeta> = {
  pass: { badgeVariant: 'teal', label: 'Pass' },
  fail: { badgeVariant: 'rose', label: 'Fail' },
  na: { badgeVariant: 'secondary', label: 'N/A' },
};

function VerdictIcon({ verdict }: { verdict: ReviewVerdict }) {
  switch (verdict) {
    case 'pass':
      return <CheckmarkFilled size={16} className="text-highlight-success" />;
    case 'fail':
      return <CloseFilled size={16} className="text-highlight-error" />;
    case 'na':
      return <SubtractAlt size={16} className="text-muted-foreground" />;
  }
}

function leftBorderClass(verdict: ReviewVerdict): string {
  switch (verdict) {
    case 'pass':
      return 'border-l-[3px] border-l-highlight-success';
    case 'fail':
      return 'border-l-[3px] border-l-highlight-error';
    case 'na':
      return 'border-l-[3px] border-l-border';
  }
}

export const RuleResultCard: React.FC<RuleResultCardProps> = ({
  ruleTitle,
  result,
  pending = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = pending || result === null;
  const verdict = ((result?.verdict as ReviewVerdict) ?? 'na');
  const sectionMatch = result?.section_match ?? null;
  const sectionMatchType = result?.section_match_type ?? null;

  const handleEvidenceClick = useCallback(
    (quote: string) => (event: React.MouseEvent) => {
      event.stopPropagation();
      void highlightEvidenceQuote(quote, sectionMatch, sectionMatchType);
    },
    [sectionMatch, sectionMatchType],
  );

  if (isPending) {
    return (
      <div className="shrink-0 bg-background border border-border rounded-md overflow-hidden mb-1.5">
        <div className="flex items-center gap-2 px-3 py-2">
          <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          <span className="flex-1 text-sm font-semibold text-foreground truncate">
            {ruleTitle}
          </span>
        </div>
      </div>
    );
  }

  const meta = VERDICT_META[verdict] ?? VERDICT_META.na;
  const hasReasoning = Boolean(result.reasoning);
  const accentClass = leftBorderClass(verdict);
  const evidence = result.evidence ?? [];

  return (
    <div
      className={`shrink-0 bg-background border border-border rounded-md overflow-hidden mb-1.5 ${accentClass}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 ${hasReasoning ? 'cursor-pointer hover:bg-muted/40' : ''}`}
        onClick={() => hasReasoning && setExpanded((v) => !v)}
        role={hasReasoning ? 'button' : undefined}
        aria-expanded={hasReasoning ? expanded : undefined}
      >
        <div className="shrink-0 flex items-center">
          <VerdictIcon verdict={verdict} />
        </div>

        <span className="flex-1 text-sm font-semibold text-foreground truncate">
          {ruleTitle}
        </span>

        <Badge variant={meta.badgeVariant}>{meta.label}</Badge>

        {hasReasoning && (
          <div className="shrink-0 text-muted-foreground">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </div>

      {expanded && hasReasoning && (
        <div className="px-3 py-2 bg-muted/40 border-t border-border space-y-3">
          <p className="text-sm leading-relaxed text-foreground/80">
            {result.reasoning}
          </p>

          {evidence.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Evidence
              </p>
              <ul className="space-y-1">
                {evidence.map((quote) => (
                  <li key={quote}>
                    <button
                      type="button"
                      className="w-full text-left text-sm text-primary underline underline-offset-2 hover:text-primary/80"
                      onClick={handleEvidenceClick(quote)}
                    >
                      &ldquo;{quote}&rdquo;
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.rule_criteria && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rule
              </p>
              <p className="text-sm leading-relaxed text-foreground/70">
                {result.rule_criteria}
              </p>
            </div>
          )}

          {result.condition_met === false && (
            <p className="text-xs text-muted-foreground italic">
              Rule condition was not met — evaluation skipped
            </p>
          )}
        </div>
      )}
    </div>
  );
};
